// Shared chrome for every unauthenticated / pre-app screen: sign in, sign up,
// email confirmation, and the onboarding questionnaire.
//
// It exists so the five screens cannot drift apart. They previously each
// re-declared the same header, column width, and input classes, which is how
// the sign-in page ended up looking nothing like the onboarding page it hands
// off to. Same brand bar, same warm paper canvas, same forest headings.
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { fontPx } from '@/lib/fontSize';

interface AuthLayoutProps {
  children: ReactNode;
  /** Column width in px. 480 for the short forms, 640 for the questionnaire. */
  width?: 480 | 640;
  /** 1-based step; renders the "Step n of m" label and the progress rail. */
  step?: number;
  totalSteps?: number;
}

export function AuthLayout({ children, width = 480, step, totalSteps }: AuthLayoutProps) {
  const maxWidth = `${width}px`;
  const showRail = typeof step === 'number' && typeof totalSteps === 'number';

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="mx-auto flex items-center gap-3 px-5 py-4" style={{ maxWidth }}>
        <img src="/cwy-mark.svg" alt="CWY" width={32} height={32} style={{ borderRadius: '8px' }} />
        <span className="text-sm font-semibold text-forest">Compete With Yourself</span>
        {showRail && (
          <span className="ml-auto text-xs font-medium text-ink-faint" data-numeric="">
            Step {step} of {totalSteps}
          </span>
        )}
      </header>

      {showRail && (
        <div className="mx-auto flex gap-1.5 px-5" style={{ maxWidth }}>
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className="h-1.5 flex-1 rounded-full transition"
              style={{ background: i < step ? 'var(--brand-forest)' : 'var(--surface-sunk)' }}
            />
          ))}
        </div>
      )}

      <main className="mx-auto px-5 py-10" style={{ maxWidth }}>
        {children}
      </main>
    </div>
  );
}

/** Page title + supporting line, in the one style the flow uses throughout. */
export function AuthHeading({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-forest">{title}</h1>
      {children && <p className="mt-1 text-ink-soft">{children}</p>}
    </div>
  );
}

const FIELD_CLASS =
  'w-full rounded-[var(--control-radius)] border border-line bg-card px-[var(--control-px-md)] text-[length:var(--control-text-md)] text-ink outline-none transition h-[var(--control-h-md)] disabled:opacity-60';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Quiet helper line under the input. */
  hint?: ReactNode;
  /** Field-level validation message; replaces the hint and reddens the border. */
  error?: string;
  containerStyle?: CSSProperties;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, containerStyle, className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const describedBy = error || hint ? `${fieldId}-note` : undefined;

  return (
    <div style={containerStyle}>
      {label && (
        <label htmlFor={fieldId} className="mb-1.5 block text-sm font-semibold text-ink">
          {label}
        </label>
      )}
      <input
        {...rest}
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={className ?? FIELD_CLASS}
        style={error ? { borderColor: 'var(--danger)', ...rest.style } : rest.style}
      />
      {(error || hint) && (
        <p
          id={describedBy}
          className={`mt-1 text-xs ${error ? 'text-danger' : 'text-ink-faint'}`}
          role={error ? 'alert' : undefined}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
});

/** Form-level error banner — for "incorrect email or password", not field rules. */
export function AuthError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p
      role="alert"
      className="rounded-card px-3.5 py-2.5 text-sm"
      style={{ background: 'var(--pink-bg)', color: 'var(--pink-text)' }}
    >
      {children}
    </p>
  );
}

/** The wide primary action the sign-in / sign-up / verify screens all use. */
export const AUTH_BUTTON_STYLE: CSSProperties = {
  width: '100%',
  padding: '13px 20px',
  fontSize: fontPx(15),
};